import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Card,
  CardContent,
  styled,
  keyframes,
  Alert,
} from "@mui/material";
import {
  Email as MailIcon,
  Lock as LockIcon,
  Login as LoginIcon,
} from "@mui/icons-material";
import Logo from "../Images/logo.jpg";
import {
  login,
  getProfile,
  isAdminRole,
  clearAuthStorage,
  extractUserFromAuthResponse,
} from "../API/Api";

// Keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled components
const LoginContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)",
    pointerEvents: "none",
  },
}));

const LoginCard = styled(Card)(({ theme }) => ({
  maxWidth: 450,
  width: "100%",
  borderRadius: "24px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.1)",
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  animation: `${fadeIn} 0.8s ease-out`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
    pointerEvents: "none",
  },
}));

const LogoContainer = styled(Box)(() => ({
  textAlign: "center",
  mb: 4,
  animation: `${slideIn} 0.6s ease-out 0.2s both`,
}));

const LogoImage = styled("img")(() => ({
  width: 120,
  height: 120,
  borderRadius: "50%",
  objectFit: "contain",

  padding: "4px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  border: "4px solid rgba(255, 255, 255, 0.8)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
  },
}));

const LoginTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "2.5rem",
  background: "linear-gradient(45deg, #667eea 0%, #764ba2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "center",
  mb: 1,
  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const LoginSubtitle = styled(Typography)(({ theme }) => ({
  color: "#666",
  textAlign: "center",
  mb: 4,
  fontSize: "1.1rem",
  fontWeight: 400,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    "& fieldset": {
      borderColor: "rgba(102, 126, 234, 0.3)",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(102, 126, 234, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#667eea",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#667eea",
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #667eea 0%, #764ba2 100%)",
  borderRadius: "12px",
  padding: "14px 32px",
  fontWeight: 600,
  fontSize: "1.1rem",
  textTransform: "none",
  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
  "&:hover": {
    background: "linear-gradient(45deg, #764ba2 0%, #667eea 100%)",
    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.6)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const Admin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      const data = await login(email.trim(), password);
      const token =
        data?.token || data?.data?.token || data?.access_token;

      if (!token) {
        throw new Error("Login succeeded but no token was returned.");
      }

      const loginUser = extractUserFromAuthResponse(data);
      let role = loginUser?.role;

      if (!role) {
        localStorage.setItem("sessionToken", token);
        localStorage.setItem("token", token);
        const profile = await getProfile();
        role = profile?.role;
      }

      if (!isAdminRole(role)) {
        clearAuthStorage();
        throw new Error("Access denied. Only admin users can sign in.");
      }

      localStorage.setItem("sessionToken", token);
      localStorage.setItem("token", token);
      localStorage.setItem("userRole", role);
      window.location.href = "/";
    } catch (err) {
      setError(err.message || "Invalid credentials. Please check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <LoginContainer maxWidth={false}>
      <LoginCard elevation={0}>
        <CardContent sx={{ p: 5, position: "relative", zIndex: 1 }}>
          <LogoContainer>
            <LogoImage src={Logo} alt="Company Logo" />
          </LogoContainer>

          <LoginTitle variant="h3" component="h1">
            Welcome Back
          </LoginTitle>
          <LoginSubtitle variant="h6">
            Sign in to your admin account
          </LoginSubtitle>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: "12px",
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                border: "1px solid rgba(244, 67, 54, 0.2)",
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <StyledTextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <MailIcon sx={{ mr: 1, color: "#667eea" }} />,
                }}
                required
                disabled={loading}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <StyledTextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: "#667eea" }} />,
                }}
                required
                disabled={loading}
              />
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <LoginButton
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={<LoginIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                }}
              >
                {loading ? "Signing In..." : "Sign In"}
              </LoginButton>
            </Box>
          </Box>
        </CardContent>
      </LoginCard>
    </LoginContainer>
  );
};

export default Admin;
